import React, { useEffect, useRef, useState } from "react";
import { SectionWrapper } from "./wrapper";

const ESSENTIALS_PRICE_YR = 30000;  // $2,500/mo
const ADVANCED_PRICE_YR = 84000;    // $7,000/mo
const ENTERPRISE_PRICE_YR = 240000;  // $20,000/mo

export const EnrichmentCalculator = ({ section }) => {
  const calculatorRef = useRef();
  const [selectedPlan, setSelectedPlan] = useState('essentials');
  const [showValues, setShowValues] = useState(false);

  const formatNumber = (num) => {
    return "$" + num.toLocaleString();
  };

  const handleInputChange = (inputId, sliderId, min, max) => {
    if (!calculatorRef.current) return;

    const input = calculatorRef.current.querySelector(`#${inputId}`);
    const slider = calculatorRef.current.querySelector(`#${sliderId}`);

    let value = parseInt(input.value) || 0;

    // Clamp value to min/max
    value = Math.max(min, Math.min(max, value));

    // Update both input and slider
    input.value = value;
    slider.value = value;

    calculate();
  };

  const handleInputBlur = (inputId, sliderId, min, max) => {
    if (!calculatorRef.current) return;

    const input = calculatorRef.current.querySelector(`#${inputId}`);
    const slider = calculatorRef.current.querySelector(`#${sliderId}`);

    let value = parseInt(input.value) || 0;

    // Clamp value to min/max
    value = Math.max(min, Math.min(max, value));

    // Update both input and slider
    input.value = value;
    slider.value = value;

    calculate();
  };

  const handleInputKeyPress = (event, inputId, sliderId, min, max) => {
    if (event.key === 'Enter') {
      handleInputBlur(inputId, sliderId, min, max);
    }
  };

  const handleSliderChange = (sliderId, inputId) => {
    if (!calculatorRef.current) return;

    const slider = calculatorRef.current.querySelector(`#${sliderId}`);
    const input = calculatorRef.current.querySelector(`#${inputId}`);

    input.value = slider.value;
    calculate();
  };

  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan);
    calculate(plan);
  };

  const calculate = (planOverride) => {
    if (!calculatorRef.current) return;

    const plan = planOverride !== undefined ? planOverride : selectedPlan;

    const orgs = +calculatorRef.current.querySelector('#orgs').value;
    const contacts = +calculatorRef.current.querySelector('#contacts').value;
    const refreshes = +calculatorRef.current.querySelector('#refreshes').value;
    const fieldsOrg = +calculatorRef.current.querySelector('#fields_org').value;
    const fieldsContact = +calculatorRef.current.querySelector('#fields_contact').value;

    // Update display values and sync sliders/inputs
    calculatorRef.current.querySelector('#orgs-display').value = orgs;
    calculatorRef.current.querySelector('#contacts-display').value = contacts;
    calculatorRef.current.querySelector('#fields_org-display').value = fieldsOrg;
    calculatorRef.current.querySelector('#fields_contact-display').value = fieldsContact;
    calculatorRef.current.querySelector('#refreshes-display').value = refreshes;

    let platformFeeCR;
    if (plan === 'essentials') {
      platformFeeCR = ESSENTIALS_PRICE_YR;
    } else if (plan === 'advanced') {
      platformFeeCR = ADVANCED_PRICE_YR;
    } else {
      platformFeeCR = ENTERPRISE_PRICE_YR;
    }

    const costPerOrgCR = 0.30;
    const costPerContactCR = 0.11;
    const platformFreeOrgs = 5000;
    const platformFreeContacts = 200000;
    const supportCR = 37500;
    const supportClay = 150000;
    const costClayCredit = 0.015;

    const totalCredits = refreshes * ((orgs * fieldsOrg) + (contacts * fieldsContact));
    const clayCost = totalCredits * costClayCredit;

    const incrOrgsCR = Math.max(0, orgs - platformFreeOrgs) * costPerOrgCR;
    const incrContactsCR = Math.max(0, contacts - platformFreeContacts) * costPerContactCR;
    const enrichCR = incrOrgsCR + incrContactsCR;
    const enrichClay = clayCost;

    const totalCR = platformFeeCR + enrichCR + supportCR;
    const totalClay = enrichClay + supportClay;

    calculatorRef.current.querySelector('.cr-org').textContent = formatNumber(incrOrgsCR);
    calculatorRef.current.querySelector('.clay-org').textContent = formatNumber(75000);
    calculatorRef.current.querySelector('.cr-contact').textContent = formatNumber(incrContactsCR);
    calculatorRef.current.querySelector('.clay-contact').textContent = formatNumber(150000);
    calculatorRef.current.querySelector('.cr-enrich').textContent = formatNumber(enrichCR);
    calculatorRef.current.querySelector('.clay-enrich').textContent = formatNumber(enrichClay);
    calculatorRef.current.querySelector('.cr-support').textContent = formatNumber(supportCR);
    calculatorRef.current.querySelector('.clay-support').textContent = formatNumber(supportClay);
    calculatorRef.current.querySelector('.cr-total').textContent = formatNumber(totalCR);
    calculatorRef.current.querySelector('.clay-total').textContent = formatNumber(totalClay);

    // Update platform fee display
    calculatorRef.current.querySelector('.cr-platform').textContent = formatNumber(platformFeeCR);

    // Update total display at top
    calculatorRef.current.querySelector('.total-cr').textContent = formatNumber(totalCR);
    calculatorRef.current.querySelector('.total-clay').textContent = formatNumber(totalClay);

    // Update savings indicator
    const savings = totalClay - totalCR;
    const savingsElement = calculatorRef.current.querySelector('.savings-amount');
    const savingsValueElement = calculatorRef.current.querySelector('.savings-value');
    if (savings > 0) {
      savingsValueElement.textContent = formatNumber(savings);
      savingsElement.style.display = 'block';
    } else {
      savingsElement.style.display = 'none';
    }
  };

  useEffect(() => {
    calculate();
  }, []);

  return (
    <SectionWrapper {...section}>
      <div ref={calculatorRef}>
        <style jsx>{`
          .calc-container {
            background: linear-gradient(135deg, rgba(20, 20, 30, 0.95) 0%, rgba(30, 30, 45, 0.95) 100%);
            border-radius: 16px;
            box-shadow: 0 12px 40px rgba(0, 0, 0, 0.4);
            backdrop-filter: blur(10px);
            border: 1px solid rgba(255, 255, 255, 0.1);
            padding: 12px;
            margin: 0 auto;
          }
          
          .total-display {
            background: rgba(27, 200, 140, 0.1);
            border: 2px solid rgba(27, 200, 140, 0.3);
            border-radius: 16px;
            padding: 24px;
            margin-bottom: 32px;
            text-align: center;
          }
          
          .total-display h2 {
            color: #ffffff;
            font-size: 18px;
            font-weight: 500;
            margin: 0 0 8px 0;
            opacity: 0.8;
          }
          
          .total-amounts {
            display: flex;
            justify-content: space-around;
            align-items: center;
            gap: 32px;
          }
          
          .total-item {
            text-align: center;
          }
          
          .total-label {
            color: rgba(255, 255, 255, 0.7);
            font-size: 14px;
            font-weight: 500;
            margin-bottom: 4px;
          }
          
          .total-value {
            font-size: 32px;
            font-weight: 700;
            line-height: 1;
          }
          
          .total-value.common-room {
            color: #1BC88C;
          }
          
          .total-value.clay {
            color: #ff6450;
          }
          
          .savings-indicator {
            text-align: center;
            background: none;
            border: none;
            padding: 0;
            margin: 0;
          }
          
          .savings-value {
            font-size: 32px;
            font-weight: 700;
            line-height: 1;
            color: #ffffff;
          }
          
          .input-section {
            margin-bottom: 32px;
          }
          
          .input-row {
            display: grid;
            gap: 20px;
            margin-bottom: 20px;
          }
          
          .input-row.large {
            grid-template-columns: 1fr 1fr;
          }
          
          .input-row.small {
            grid-template-columns: 1fr 1fr 1fr;
          }
          
          @media (max-width: 1024px) {
            .input-row.large,
            .input-row.small {
              grid-template-columns: 1fr;
            }
          }
          
          .input-group {
            background: rgba(40, 40, 55, 0.6);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 12px;
            padding: 20px;
            margin-bottom: 16px;
            transition: all 0.3s ease;
          }
          
          .input-group:hover {
            background: rgba(45, 45, 60, 0.7);
            border-color: rgba(27, 200, 140, 0.3);
          }
          
          .input-label {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 12px;
            color: #ffffff;
            font-weight: 500;
            font-size: 16px;
          }
          
          .input-value {
            background: transparent;
            color: #1BC88C;
            padding: 6px 16px;
            border-radius: 6px;
            font-weight: 700;
            border: none;
            min-width: 100px;
            text-align: right;
            outline: none;
            transition: all 0.2s ease;
            font-size: 15px;
            cursor: pointer;
            -moz-appearance: textfield;
          }
          
          .input-value::-webkit-outer-spin-button,
          .input-value::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
          }
          
          .input-value:focus {
            background: rgba(27, 200, 140, 0.1);
            color: #20d898;
          }
          
          .input-value:hover {
            background: rgba(27, 200, 140, 0.05);
          }
          
          .calc-slider {
            width: 100%;
            height: 6px;
            border-radius: 6px;
            background: linear-gradient(90deg, rgba(60, 60, 75, 0.8) 0%, rgba(27, 200, 140, 0.2) 100%);
            outline: none;
            transition: all 0.2s ease;
            -webkit-appearance: none;
            appearance: none;
            position: relative;
          }
          
          .slider-container {
            position: relative;
            width: 100%;
          }
          
          .slider-ticks {
            position: absolute;
            top: 10px;
            left: 0;
            width: 100%;
            height: 20px;
            pointer-events: none;
          }
          
          .tick {
            position: absolute;
            width: 1px;
            height: 8px;
            background: rgba(255, 255, 255, 0.3);
            transform: translateX(-50%);
          }
          
          .tick.major {
            height: 12px;
            background: rgba(255, 255, 255, 0.5);
          }
          
          .tick-label {
            position: absolute;
            top: 14px;
            font-size: 10px;
            color: rgba(255, 255, 255, 0.6);
            transform: translateX(-50%);
            white-space: nowrap;
          }
          
          .calc-slider::-webkit-slider-thumb {
            -webkit-appearance: none;
            appearance: none;
            width: 18px;
            height: 18px;
            border-radius: 50%;
            background: #1BC88C;
            cursor: pointer;
            border: 2px solid rgba(255, 255, 255, 0.2);
            transition: all 0.2s ease;
            box-shadow: 0 2px 8px rgba(27, 200, 140, 0.3);
          }
          
          .calc-slider::-webkit-slider-thumb:hover {
            transform: scale(1.1);
            box-shadow: 0 4px 12px rgba(27, 200, 140, 0.5);
          }
          
          .calc-slider::-moz-range-thumb {
            width: 18px;
            height: 18px;
            border-radius: 50%;
            background: #1BC88C;
            cursor: pointer;
            border: 2px solid rgba(255, 255, 255, 0.2);
            transition: all 0.2s ease;
          }
          
          .comparison-section {
            background: rgba(25, 25, 35, 0.8);
            border-radius: 12px;
            border: 1px solid rgba(255, 255, 255, 0.1);
            overflow: hidden;
          }
          
          .comparison-table {
            width: 100%;
            border-collapse: collapse;
          }
          
          .comparison-table th,
          .comparison-table td {
            padding: 16px 20px;
            border-bottom: 1px solid rgba(255, 255, 255, 0.08);
            text-align: left;
            color: #ffffff;
          }
          
          .comparison-table th:first-child {
            background: rgba(30, 30, 40, 0.9);
            color: #ffffff;
            width: 40%;
            font-weight: 600;
          }
          
          .comparison-table th:nth-child(2) {
            background: rgba(27, 200, 140, 0.15);
            color: #1BC88C;
            width: 30%;
            font-weight: 700;
            position: relative;
          }
          
          .comparison-table th:nth-child(3) {
            background: rgba(255, 100, 80, 0.15);
            color: #ff6450;
            width: 30%;
            font-weight: 600;
            position: relative;
          }
          
          .comparison-table td:first-child {
            width: 40%;
            font-weight: 500;
            color: rgba(255, 255, 255, 0.9);
          }
          
          .comparison-table td:nth-child(2) {
            color: #1BC88C;
            width: 30%;
            font-weight: 600;
            background: rgba(27, 200, 140, 0.05);
          }

          .comparison-table td:nth-child(3) {
            color: #ff6450;
            width: 30%;
            font-weight: 600;
            background: rgba(255, 100, 80, 0.05);
          }
          
          .comparison-table tr:last-child td {
            border-bottom: none;
          }
          
          .comparison-table tr:hover td {
            background: rgba(255, 255, 255, 0.02);
          }
          
          .result {
            font-weight: 700;
            font-size: 1.1em;
            padding: 4px 8px;
            border-radius: 6px;
            transition: all 0.2s ease;
          }
          
          .comparison-table td:nth-child(2) .result {
            background: rgba(27, 200, 140, 0.1);
            border: 1px solid rgba(27, 200, 140, 0.2);
          }
          
          .comparison-table td:nth-child(3) .result {
            background: rgba(255, 100, 80, 0.1);
            border: 1px solid rgba(255, 100, 80, 0.2);
          }
          
          .calc-note {
            color: rgba(255, 255, 255, 0.7);
            text-align: center;
            background: rgba(27, 200, 140, 0.08);
            padding: 20px;
            border-radius: 12px;
            border: 1px solid rgba(27, 200, 140, 0.2);
            margin-top: 24px;
          }
          
          .calc-note a {
            color: #1BC88C;
            text-decoration: none;
            font-weight: 600;
          }
          
          .calc-note a:hover {
            text-decoration: underline;
          }

          .show-values-toggle {
            display: block;
            text-align: center;
            margin-top: 16px;
            font-size: 11px;
            color: rgba(255, 255, 255, 0.25);
            cursor: pointer;
            background: none;
            border: none;
            padding: 0;
          }

          .show-values-toggle:hover {
            color: rgba(255, 255, 255, 0.5);
          }

          .values-panel {
            margin-top: 12px;
            background: rgba(0, 0, 0, 0.4);
            border: 1px solid rgba(255, 255, 255, 0.08);
            border-radius: 10px;
            padding: 16px 20px;
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 6px 24px;
          }

          .values-row {
            display: contents;
          }

          .values-key {
            font-size: 11px;
            color: rgba(255, 255, 255, 0.4);
            font-family: monospace;
            padding: 3px 0;
          }

          .values-val {
            font-size: 11px;
            color: rgba(255, 255, 255, 0.65);
            font-family: monospace;
            text-align: right;
            padding: 3px 0;
          }

          .plan-picker {
            display: flex;
            gap: 12px;
            margin-bottom: 24px;
          }

          .plan-btn {
            flex: 1;
            padding: 14px 16px;
            border-radius: 10px;
            border: 1px solid rgba(255, 255, 255, 0.15);
            background: rgba(40, 40, 55, 0.6);
            color: rgba(255, 255, 255, 0.7);
            cursor: pointer;
            text-align: center;
            transition: all 0.2s ease;
          }

          .plan-btn:hover {
            border-color: rgba(27, 200, 140, 0.4);
            color: #ffffff;
          }

          .plan-btn.active {
            border-color: #1BC88C;
            background: rgba(27, 200, 140, 0.15);
            color: #1BC88C;
          }

          .plan-btn-name {
            font-size: 15px;
            font-weight: 600;
            line-height: 1;
            margin-bottom: 4px;
          }

          .plan-btn-price {
            font-size: 12px;
            opacity: 0.75;
          }

          .plan-disclaimer {
            font-size: 12px;
            color: rgba(255, 255, 255, 0.4);
            text-align: center;
            margin: -12px 0 24px;
          }

          @media (max-width: 600px) {
            .plan-picker {
              flex-direction: column;
            }
          }
        `}</style>

        <div className="calc-container">
          {section.header && (
            <div className="text-center mb-8">
              <h1 className="text-white text-4xl font-bold mb-2">
                {section.header}
              </h1>
              <p className="text-gray-400 text-lg">
                Compare enrichment costs with elegant precision
              </p>
            </div>
          )}

          <div className="total-display">
            <div className="total-amounts">
              <div className="total-item">
                <div className="total-label">Common Room</div>
                <div className="total-value common-room total-cr">$60,000</div>
              </div>
              <div className="savings-indicator savings-amount" style={{ display: 'none' }}>
                <div className="total-label">Save</div>
                <div className="savings-value">$90,000</div>
              </div>
              <div className="total-item">
                <div className="total-label">Clay</div>
                <div className="total-value clay total-clay">$150,000</div>
              </div>
            </div>
          </div>

          <div className="plan-picker">
            {[
              { key: 'essentials', label: 'Essentials', price: '$2,500/mo' },
              { key: 'advanced',   label: 'Advanced',   price: 'Custom'    },
              { key: 'enterprise', label: 'Enterprise', price: 'Custom'     },
            ].map(({ key, label, price }) => (
              <button
                key={key}
                className={`plan-btn${selectedPlan === key ? ' active' : ''}`}
                onClick={() => handlePlanSelect(key)}
              >
                <div className="plan-btn-name">{label}</div>
                <div className="plan-btn-price">{price}</div>
              </button>
            ))}
          </div>
          <p className="plan-disclaimer">Costs shown are non-binding, ball-park estimates to help with planning.</p>

          <div className="input-section">
            <div className="input-row large">
              <div className="input-group">
                <div className="input-label">
                  <span>Organizations</span>
                  <input
                    id="orgs-display"
                    type="number"
                    min="0"
                    max="500000"
                    defaultValue="50000"
                    className="input-value"
                    onBlur={() => handleInputBlur('orgs-display', 'orgs', 0, 500000)}
                    onKeyPress={(e) => handleInputKeyPress(e, 'orgs-display', 'orgs', 0, 500000)}
                  />
                </div>
                <div className="slider-container">
                  <input
                    id="orgs"
                    type="range"
                    min="0"
                    max="500000"
                    step="100"
                    defaultValue="50000"
                    className="calc-slider"
                    onChange={() => handleSliderChange('orgs', 'orgs-display')}
                  />
                  <div className="slider-ticks">
                    <div className="tick major" style={{ left: '0%' }}></div>
                    <div className="tick-label" style={{ left: '0%' }}>0</div>
                    <div className="tick" style={{ left: '20%' }}></div>
                    <div className="tick-label" style={{ left: '20%' }}>100K</div>
                    <div className="tick" style={{ left: '40%' }}></div>
                    <div className="tick-label" style={{ left: '40%' }}>200K</div>
                    <div className="tick" style={{ left: '60%' }}></div>
                    <div className="tick-label" style={{ left: '60%' }}>300K</div>
                    <div className="tick" style={{ left: '80%' }}></div>
                    <div className="tick-label" style={{ left: '80%' }}>400K</div>
                    <div className="tick major" style={{ left: '100%' }}></div>
                    <div className="tick-label" style={{ left: '100%' }}>500K</div>
                  </div>
                </div>
              </div>

              <div className="input-group">
                <div className="input-label">
                  <span>Contacts</span>
                  <input
                    id="contacts-display"
                    type="number"
                    min="0"
                    max="5000000"
                    defaultValue="500000"
                    className="input-value"
                    onBlur={() => handleInputBlur('contacts-display', 'contacts', 0, 5000000)}
                    onKeyPress={(e) => handleInputKeyPress(e, 'contacts-display', 'contacts', 0, 5000000)}
                  />
                </div>
                <div className="slider-container">
                  <input
                    id="contacts"
                    type="range"
                    min="0"
                    max="5000000"
                    step="100"
                    defaultValue="500000"
                    className="calc-slider"
                    onChange={() => handleSliderChange('contacts', 'contacts-display')}
                  />
                  <div className="slider-ticks">
                    <div className="tick major" style={{ left: '0%' }}></div>
                    <div className="tick-label" style={{ left: '0%' }}>0</div>
                    <div className="tick" style={{ left: '20%' }}></div>
                    <div className="tick-label" style={{ left: '20%' }}>1M</div>
                    <div className="tick" style={{ left: '40%' }}></div>
                    <div className="tick-label" style={{ left: '40%' }}>2M</div>
                    <div className="tick" style={{ left: '60%' }}></div>
                    <div className="tick-label" style={{ left: '60%' }}>3M</div>
                    <div className="tick" style={{ left: '80%' }}></div>
                    <div className="tick-label" style={{ left: '80%' }}>4M</div>
                    <div className="tick major" style={{ left: '100%' }}></div>
                    <div className="tick-label" style={{ left: '100%' }}>5M</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="input-row small">
              <div className="input-group">
                <div className="input-label">
                  <span>Organization Fields</span>
                  <input
                    id="fields_org-display"
                    type="number"
                    min="0"
                    max="75"
                    defaultValue="25"
                    className="input-value"
                    onBlur={() => handleInputBlur('fields_org-display', 'fields_org', 0, 75)}
                    onKeyPress={(e) => handleInputKeyPress(e, 'fields_org-display', 'fields_org', 0, 75)}
                  />
                </div>
                <div className="slider-container">
                  <input
                    id="fields_org"
                    type="range"
                    min="0"
                    max="75"
                    defaultValue="25"
                    className="calc-slider"
                    onChange={() => handleSliderChange('fields_org', 'fields_org-display')}
                  />
                  <div className="slider-ticks">
                    <div className="tick major" style={{ left: '0%' }}></div>
                    <div className="tick-label" style={{ left: '0%' }}>0</div>
                    <div className="tick" style={{ left: '13.33%' }}></div>
                    <div className="tick-label" style={{ left: '13.33%' }}>10</div>
                    <div className="tick" style={{ left: '26.67%' }}></div>
                    <div className="tick-label" style={{ left: '26.67%' }}>20</div>
                    <div className="tick" style={{ left: '40%' }}></div>
                    <div className="tick-label" style={{ left: '40%' }}>30</div>
                    <div className="tick major" style={{ left: '53.33%' }}></div>
                    <div className="tick-label" style={{ left: '53.33%' }}>40</div>
                    <div className="tick" style={{ left: '80%' }}></div>
                    <div className="tick-label" style={{ left: '80%' }}>60</div>
                    <div className="tick major" style={{ left: '100%' }}></div>
                    <div className="tick-label" style={{ left: '100%' }}>75</div>
                  </div>
                </div>
              </div>

              <div className="input-group">
                <div className="input-label">
                  <span>Contact Fields</span>
                  <input
                    id="fields_contact-display"
                    type="number"
                    min="0"
                    max="15"
                    defaultValue="5"
                    className="input-value"
                    onBlur={() => handleInputBlur('fields_contact-display', 'fields_contact', 0, 15)}
                    onKeyPress={(e) => handleInputKeyPress(e, 'fields_contact-display', 'fields_contact', 0, 15)}
                  />
                </div>
                <div className="slider-container">
                  <input
                    id="fields_contact"
                    type="range"
                    min="0"
                    max="15"
                    defaultValue="5"
                    className="calc-slider"
                    onChange={() => handleSliderChange('fields_contact', 'fields_contact-display')}
                  />
                  <div className="slider-ticks">
                    <div className="tick major" style={{ left: '0%' }}></div>
                    <div className="tick-label" style={{ left: '0%' }}>0</div>
                    <div className="tick" style={{ left: '20%' }}></div>
                    <div className="tick-label" style={{ left: '20%' }}>3</div>
                    <div className="tick" style={{ left: '40%' }}></div>
                    <div className="tick-label" style={{ left: '40%' }}>6</div>
                    <div className="tick major" style={{ left: '60%' }}></div>
                    <div className="tick-label" style={{ left: '60%' }}>9</div>
                    <div className="tick" style={{ left: '80%' }}></div>
                    <div className="tick-label" style={{ left: '80%' }}>12</div>
                    <div className="tick major" style={{ left: '100%' }}></div>
                    <div className="tick-label" style={{ left: '100%' }}>15</div>
                  </div>
                </div>
              </div>

              <div className="input-group">
                <div className="input-label">
                  <span>Refreshes per year</span>
                  <input
                    id="refreshes-display"
                    type="number"
                    min="0"
                    max="12"
                    defaultValue="4"
                    className="input-value"
                    onBlur={() => handleInputBlur('refreshes-display', 'refreshes', 0, 12)}
                    onKeyPress={(e) => handleInputKeyPress(e, 'refreshes-display', 'refreshes', 0, 12)}
                  />
                </div>
                <div className="slider-container">
                  <input
                    id="refreshes"
                    type="range"
                    min="0"
                    max="12"
                    defaultValue="4"
                    className="calc-slider"
                    onChange={() => handleSliderChange('refreshes', 'refreshes-display')}
                  />
                  <div className="slider-ticks">
                    <div className="tick major" style={{ left: '0%' }}></div>
                    <div className="tick-label" style={{ left: '0%' }}>0</div>
                    <div className="tick" style={{ left: '25%' }}></div>
                    <div className="tick-label" style={{ left: '25%' }}>3</div>
                    <div className="tick major" style={{ left: '50%' }}></div>
                    <div className="tick-label" style={{ left: '50%' }}>6</div>
                    <div className="tick" style={{ left: '75%' }}></div>
                    <div className="tick-label" style={{ left: '75%' }}>9</div>
                    <div className="tick major" style={{ left: '100%' }}></div>
                    <div className="tick-label" style={{ left: '100%' }}>12</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="comparison-section">
            <table className="comparison-table">
              <thead>
                <tr>
                  <th></th>
                  <th>Common Room</th>
                  <th>Clay</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Incremental orgs</td>
                  <td className="cr-org"></td>
                  <td className="clay-org"></td>
                </tr>
                <tr>
                  <td>Incremental contacts</td>
                  <td className="cr-contact"></td>
                  <td className="clay-contact"></td>
                </tr>
                <tr>
                  <td>Enrichment cost</td>
                  <td className="cr-enrich result"></td>
                  <td className="clay-enrich result"></td>
                </tr>
                <tr>
                  <td>Platform Fee</td>
                  <td className="cr-platform">$60,000</td>
                  <td>$0</td>
                </tr>
                <tr>
                  <td>Headcount support</td>
                  <td className="cr-support"></td>
                  <td className="clay-support"></td>
                </tr>
                <tr>
                  <td>Total cost</td>
                  <td className="cr-total result"></td>
                  <td className="clay-total result"></td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="calc-note">
            <a href="/pricing/">
              See transparent pricing details for Common Room
            </a>
          </div>

          <button className="show-values-toggle" onClick={() => setShowValues(v => !v)}>
            {showValues ? 'Hide values' : 'Show values'}
          </button>

          {showValues && (
            <div className="values-panel">
              {[
                ['Essentials platform fee', '$2,500/mo'],
                ['Advanced platform fee',   '$7,000/mo'],
                ['Enterprise platform fee', '$20,000/mo'],
                ['Cost per org (incremental)',     '$0.30'],
                ['Cost per contact (incremental)', '$0.11'],
                ['Free orgs included',       '5,000'],
                ['Free contacts included',   '200,000'],
                ['CR headcount support/yr',  '$37,500'],
                ['Clay headcount support/yr','$150,000'],
                ['Clay credit cost',         '$0.015'],
              ].map(([key, val]) => (
                <React.Fragment key={key}>
                  <span className="values-key">{key}</span>
                  <span className="values-val">{val}</span>
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
      </div>
    </SectionWrapper>
  );
};